import React, { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Mail, Phone, MapPin, ShieldCheck, Award, Sparkles } from "lucide-react";

const TEAM_IMG = "/images/ritika-about.jpg";

export function About() {
  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.18em] text-accent font-bold">About</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">Tax filing should not feel intimidating.</h1>
          <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
            CA Ritika Singh is an Indian Chartered Accountancy practice that combined its rigour with modern AI tooling to make tax filing genuinely simple. We file returns for thousands of salaried professionals, freelancers and small businesses across India every year.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-2 gap-10 items-center">
          <img src={TEAM_IMG} alt="CA Ritika Singh" className="w-full h-[520px] object-cover object-[center_25%] rounded-xl border border-border" />
          <div>
            <h2 className="font-display text-2xl font-semibold">A practising CA. A patient AI. One platform.</h2>
            <p className="mt-3 text-muted-foreground leading-relaxed">Our founder is a Chartered Accountant with 12+ years of experience in direct taxation. We built this product because the existing options either over-promised AI magic or under-delivered on accountability. CA Ritika Singh aims to do both — be technically rigorous and feel effortless to use.</p>
            <ul className="mt-6 space-y-3 text-sm">
              <li className="flex gap-2"><Award className="h-5 w-5 text-accent shrink-0" /> ICAI-registered practising CA firm</li>
              <li className="flex gap-2"><ShieldCheck className="h-5 w-5 text-accent shrink-0" /> AES-256 encryption, DPDP-ready</li>
              <li className="flex gap-2"><Sparkles className="h-5 w-5 text-accent shrink-0" /> AI trained on the entire Indian Income Tax code</li>
            </ul>
          </div>
        </div>

        <div className="mt-20 grid md:grid-cols-3 gap-6">
          {[
            { n: "12+ yrs", l: "in direct taxation" },
            { n: "₹420 Cr+", l: "tax computed" },
            { n: "280+", l: "cities served" },
          ].map(s => (
            <Card key={s.n} className="p-8 border-border text-center">
              <div className="font-display text-4xl font-bold num-rupee">{s.n}</div>
              <div className="mt-2 text-sm text-muted-foreground">{s.l}</div>
            </Card>
          ))}
        </div>
      </main>
      <Footer />
    </div>
  );
}

export function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Got it — we'll be in touch within one business day.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch { toast.error("Couldn't send. Please email us directly."); }
    finally { setSending(false); }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-2 gap-10">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-accent font-bold">Contact</div>
            <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-2">We'd love to hear from you.</h1>
            <p className="mt-4 text-muted-foreground leading-relaxed">Have a question about pricing, an integration request, or need help with a notice? Drop us a line.</p>
            <div className="mt-8 space-y-4 text-sm">
              <div className="flex items-center gap-3"><Mail className="h-4 w-4 text-accent" /> hello@caritikasingh.com</div>
              <div className="flex items-center gap-3"><Phone className="h-4 w-4 text-accent" /> +91 80 4000 0000</div>
              <div className="flex items-center gap-3"><MapPin className="h-4 w-4 text-accent" /> 4th Floor, Embassy Tech Square, Outer Ring Road, Bengaluru 560103</div>
            </div>
          </div>

          <Card className="p-6 border-border">
            <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
              <div>
                <Label>Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required className="h-11 mt-1.5" data-testid="contact-name" />
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label>Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required className="h-11 mt-1.5" data-testid="contact-email" />
                </div>
                <div>
                  <Label>Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-11 mt-1.5" />
                </div>
              </div>
              <div>
                <Label>Message</Label>
                <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={5} required className="mt-1.5" data-testid="contact-message" />
              </div>
              <Button type="submit" disabled={sending} className="w-full h-11" data-testid="contact-submit">{sending ? "Sending…" : "Send message"}</Button>
            </form>
          </Card>
        </div>
      </main>
      <Footer />
    </div>
  );
}
